import { Component } from '@theme/component';
import { WishlistEvents, getWishlist, removeFromWishlist } from '@theme/wishlist';

/**
 * Renders the wishlist page. The server can't see localStorage, so on connect
 * we read the saved handles and fetch each product card from the
 * `product.wishlist-card` alternate template (server-rendered — prices stay in
 * Liquid). Dead handles (deleted / unavailable products) are pruned.
 *
 * Markup lives in `sections/wishlist.liquid`.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} loading - Loading state.
 * @property {HTMLElement} empty - Empty state.
 * @property {HTMLUListElement} grid - The saved-items grid.
 * @extends {Component<Refs>}
 */
class WishlistPage extends Component {
  requiredRefs = ['loading', 'empty', 'grid'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(WishlistEvents.update, this.#onUpdate);
    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistEvents.update, this.#onUpdate);
  }

  /** @param {CustomEvent} event */
  #onUpdate = (event) => {
    const handle = event.detail?.handle;
    if (event.detail?.added === false && handle) {
      this.refs.grid.querySelector(`[data-wishlist-handle="${CSS.escape(handle)}"]`)?.remove();
      this.#syncEmptyState();
    }
  };

  async #load() {
    const handles = getWishlist();
    this.#setState('loading');

    if (handles.length === 0) {
      this.#setState('empty');
      return;
    }

    const root = window.Shopify?.routes?.root || '/';
    const results = await Promise.all(handles.map((handle) => this.#fetchCard(root, handle)));

    const fragment = document.createDocumentFragment();
    for (const result of results) {
      if (result?.node) fragment.append(result.node);
      else if (result?.prune) removeFromWishlist(result.prune);
    }

    this.refs.grid.append(fragment);
    this.#syncEmptyState();
  }

  /**
   * @param {string} root
   * @param {string} handle
   * @returns {Promise<{node?: Element, prune?: string} | null>}
   */
  async #fetchCard(root, handle) {
    try {
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}?view=wishlist-card`);
      if (!response.ok) return { prune: handle };

      const html = (await response.text()).trim();
      if (!html) return { prune: handle };

      const template = document.createElement('template');
      template.innerHTML = html;
      const node = template.content.firstElementChild;
      return node ? { node } : { prune: handle };
    } catch (error) {
      // Network error — keep the handle, just skip rendering it this time.
      return null;
    }
  }

  #syncEmptyState() {
    this.#setState(this.refs.grid.children.length > 0 ? 'list' : 'empty');
  }

  /** @param {'loading' | 'empty' | 'list'} state */
  #setState(state) {
    this.refs.loading.hidden = state !== 'loading';
    this.refs.empty.hidden = state !== 'empty';
    this.refs.grid.hidden = state !== 'list';
  }
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}
